// Live artwork thumbnail for the configurator spec-summary mockup (ProjectMockup).
//
// Picks the best customer-uploaded face for the current family and renders it to
// an <img>-able URL so the right-rail mockup can wear the customer's actual
// artwork — the "upgrade" half of the baseline+upgrade summary art:
//   - booklet families  → the front COVER (a 'cover' upload, or page 1 of a
//                         'combined' Hands-on Prep pile)
//   - single sheet      → the flat artwork FACE (the 'general' upload)
//   - assembly          → the finished-kit PHOTO, shown in place of the carton
// Every other family (labels, USB) has no paint-on face → null, and the mockup
// shows its branded per-type placeholder instead.
//
// Source priority within a family (first renderable wins):
//   booklet:  1. a 'cover' file being uploaded right now (pending)
//             2. an uploaded 'cover' asset
//             3. a 'combined' pending upload   ← page 1 is usually the cover
//             4. an uploaded 'combined' asset
//   sheet:    1. a 'general' file being uploaded right now (pending)
//             2. an uploaded 'general' asset
//   assembly: 1. a kit photo being uploaded right now (pending)
//             2. the uploaded kit photo — a browser-paintable image only, so a
//                phone's HEIC or a spreadsheet of the kit keeps the carton
//
// Rendering reuses the upload tile's machinery: image bytes come through the
// same-origin preview proxy; PDFs render their first page via pdf.js
// (buildDocThumbnailUrl). This is PRESENTATION ONLY — it paints a preview and
// feeds nothing to pricing or the order (CLAUDE.md §"Pricing logic lives in the
// ERP"). It also never caches anything (CLAUDE.md §Caching): the object URLs are
// per-render ephemera, revoked on change/unmount.
//
// A cover wrap PDF (front+spine+back in one file, per COMPONENT_ROLE_HELP.cover)
// renders its whole page 1 "slice"-filled into the front face — good enough to
// recognize the piece; we don't attempt to crop out just the front panel.

import { useEffect, useRef, useState } from 'react';

import { BOOKLET_PRODUCT_TYPES, isRenderableImage } from 'shared';

import * as uploadsApi from '../api/uploads.js';
import { buildDocThumbnailUrl, docThumbnailKind } from './docThumbnail.js';
import { type LineAssets } from './useLineAssets.js';
import type { ComponentRole } from 'shared';
import type { FamilyCode } from './FamilyGlyph.js';
import { KIT_PHOTO_ROLE, type AssemblyPhotos } from './useAssemblyPhotos.js';

// A resolved, renderable artwork source. `key` changes whenever the chosen
// source changes, so the effect knows to re-render (and revoke the prior URL).
type ArtworkSource =
  // A pending image upload's local blob URL — BORROWED from useLineAssets, so we
  // render it but never revoke it (its owner does).
  | { key: string; kind: 'borrowed-url'; url: string }
  | { key: string; kind: 'image-asset'; assetId: string }
  | { key: string; kind: 'pdf-file'; file: File }
  | { key: string; kind: 'pdf-asset'; assetId: string };

function pickArtworkSource(
  lineAssets: LineAssets,
  family: FamilyCode | null,
  assemblyPhotos: AssemblyPhotos | null,
): ArtworkSource | null {
  if (!family) return null;

  // The kit photo is not a line asset: it rides useAssemblyPhotos, whose uploads
  // never reach useLineAssets' list.
  if (family === 'assembly') {
    if (!assemblyPhotos) return null;
    const p = assemblyPhotos.pending.find(
      (u) => !u.error && u.role === KIT_PHOTO_ROLE && u.preview_url,
    );
    if (p?.preview_url) return { key: `pending:${p.local_id}`, kind: 'borrowed-url', url: p.preview_url };
    const a = assemblyPhotos.kitPhotos[0];
    if (a && isRenderableImage(a.content_type)) {
      return { key: `asset:${a.id}`, kind: 'image-asset', assetId: a.id };
    }
    return null;
  }

  const renderablePending = (role: ComponentRole): ArtworkSource | null => {
    const p = lineAssets.pending.find((u) => !u.error && u.component_role === role);
    if (!p) return null;
    if (p.preview_url) return { key: `pending:${p.local_id}`, kind: 'borrowed-url', url: p.preview_url };
    if (docThumbnailKind(p.file.type, p.file.name) === 'pdf') {
      return { key: `pending:${p.local_id}`, kind: 'pdf-file', file: p.file };
    }
    return null; // zip / ai / eps artwork — no thumbnail, fall through
  };

  const renderableAsset = (role: ComponentRole): ArtworkSource | null => {
    // assets are newest-first, so find() takes the most recent of the role.
    const a = lineAssets.assets.find((x) => x.component_role === role);
    if (!a) return null;
    if (isRenderableImage(a.content_type)) return { key: `asset:${a.id}`, kind: 'image-asset', assetId: a.id };
    if (docThumbnailKind(a.content_type, a.original_filename) === 'pdf') {
      return { key: `asset:${a.id}`, kind: 'pdf-asset', assetId: a.id };
    }
    return null;
  };

  // Booklets wear their front cover (a 'combined' pile's page 1 stands in).
  if (BOOKLET_PRODUCT_TYPES.has(family)) {
    return (
      renderablePending('cover') ??
      renderableAsset('cover') ??
      renderablePending('combined') ??
      renderableAsset('combined')
    );
  }
  // Single sheet wears its flat artwork face (the 'general' upload — see
  // SchemaForm.FILE_REFERENCE_ROLE: artwork_file_reference → 'general').
  if (family === 'single_sheet') {
    return renderablePending('general') ?? renderableAsset('general');
  }
  // labels / USB have no paint-on face → branded placeholder.
  return null;
}

// Returns the artwork thumbnail object URL (or null while loading / when there's
// no renderable face). Safe to call for every family — it no-ops for the ones
// with no paint-on mockup face.
export function useArtworkPreview(
  lineAssets: LineAssets,
  family: FamilyCode | null,
  assemblyPhotos: AssemblyPhotos | null = null,
): string | null {
  const source = pickArtworkSource(lineAssets, family, assemblyPhotos);
  const sourceKey = source?.key ?? 'none';

  // The effect depends only on sourceKey (arrays change identity every render);
  // read the live descriptor through a ref so the closure isn't stale.
  const sourceRef = useRef(source);
  sourceRef.current = source;

  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    const src = sourceRef.current;
    setUrl(null);
    if (!src) return;
    // Borrowed pending preview — render as-is, don't manage its lifetime.
    if (src.kind === 'borrowed-url') {
      setUrl(src.url);
      return;
    }

    let cancelled = false;
    let created: string | null = null;
    void (async () => {
      try {
        let out: string | null = null;
        if (src.kind === 'image-asset') {
          out = await uploadsApi.fetchAssetPreviewBlobUrl(src.assetId);
        } else if (src.kind === 'pdf-file') {
          out = await buildDocThumbnailUrl(src.file);
        } else {
          const blob = await uploadsApi.fetchAssetPreviewBlob(src.assetId);
          out = await buildDocThumbnailUrl(blob);
        }
        if (cancelled) {
          if (out) URL.revokeObjectURL(out);
          return;
        }
        created = out;
        setUrl(out);
      } catch {
        if (!cancelled) setUrl(null);
      }
    })();
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
    // sourceKey fully identifies the chosen source; the live descriptor is read
    // through sourceRef so the closure isn't stale despite the narrow deps.
  }, [sourceKey]);

  return url;
}
